import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { Button, Headline, Paragraph, Section, ContentBlock } from "./ui";
import { api, transformBlogPost } from "../services/api";

// Dummy content for development
const dummyPost = {
  id: "1",
  title: "Die Zukunft des Porsche 911: Elektrisch oder klassisch?",
  slug: "zukunft-porsche-911-elektrisch-klassisch",
  publishDate: "2024-03-15",
  author: "Andreas Wutschke",
  heroImage:
    "https://res.cloudinary.com/donwh3eei/image/upload/v1756137421/gallery/frank_kayser_shooting/thomas_mbovj4.jpg",
  excerpt:
    "Eine tiefgreifende Analyse der Entwicklung des Porsche 911 und was die Zukunft für diesen Ikonen bereithält.",
  tags: ["Porsche 911", "Elektromobilität", "Klassiker"],
  content: `


Der Porsche 911 ist mehr als nur ein Auto – er ist eine Ikone, die seit über 60 Jahren die Herzen der Sportwagen-Enthusiasten höher schlagen lässt. Doch wie wird sich dieser Klassiker in einer zunehmend elektrifizierten Welt entwickeln?

## Die klassischen Wurzeln

Seit seiner Einführung im Jahr 1963 hat der 911 eine bemerkenswerte Entwicklung durchlaufen. Von den ersten luftgekühlten Sechszylindern bis zu den heutigen hochmodernen Turbomotoren – der 911 hat stets den Spagat zwischen Tradition und Innovation gemeistert.

### Charakteristische Merkmale

- **Heckmotor-Konzept**: Das einzigartige Layout mit dem Motor im Heck
- **Zeitloses Design**: Die unverwechselbare Silhouette seit über 60 Jahren
- **Fahrdynamik**: Die perfekte Balance zwischen Komfort und Sportlichkeit

## Der Weg in die Elektrifizierung

Mit dem Taycan hat Porsche bereits bewiesen, dass Elektromobilität und Sportlichkeit perfekt harmonieren können. Doch wie wird sich diese Technologie auf den 911 auswirken?

### Herausforderungen und Chancen

Die Elektrifizierung des 911 bringt sowohl Herausforderungen als auch Chancen mit sich:

**Herausforderungen:**
- Gewichtsverteilung bei Batterieintegration
- Erhaltung der charakteristischen Fahrdynamik
- Akzeptanz bei traditionellen 911-Liebhabern

**Chancen:**
- Sofortiges Drehmoment für noch bessere Performance
- Neue Möglichkeiten in der Fahrzeugarchitektur
- Umweltfreundlichkeit ohne Kompromisse bei der Leistung

## Fazit

Der Porsche 911 wird auch in Zukunft eine Legende bleiben – ob mit klassischem Verbrennungsmotor oder mit elektrischem Antrieb. Die Kunst liegt darin, die DNA des 911 zu bewahren, während man gleichzeitig den Anforderungen der Zeit gerecht wird.

*Was denkst du über die Zukunft des 911? Teile deine Meinung in den Kommentaren mit uns.*
  `,
};

const SimpleBlogPost = ({ debugMode = false }) => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        setLoading(true);
        setError(null);

        // If debugMode is enabled or no slug provided or test slug, use dummy data
        if (debugMode || !slug || slug === "test-blog") {
          setPost(dummyPost);
          setLoading(false);
          return;
        }

        // Fetch from Strapi
        const strapiPost = await api.getBlogPostBySlug(slug);

        if (strapiPost) {
          const transformedPost = transformBlogPost(strapiPost);
          setPost(transformedPost);
        } else {
          setError("Blog post not found");
        }
      } catch (err) {
        console.error("Error fetching blog post:", err);
        setError("Failed to load blog post");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [slug, debugMode]);

  const handleBackClick = () => {
    navigate(-1);
  };

  // Function to parse [GALLERY] and [ICON_LIST] tags and convert them to JSX
  const parseContentTags = (content) => {
    if (!content) return content;

    // Replace [GALLERY]...[/GALLERY] with a special marker
    const galleryRegex = /\[GALLERY\]([\s\S]*?)\[\/GALLERY\]/g;
    const galleries = [];
    let galleryIndex = 0;

    // Replace [ICON_LIST]...[/ICON_LIST] with a special marker
    const iconListRegex = /\[ICON_LIST\]([\s\S]*?)\[\/ICON_LIST\]/g;
    const iconLists = [];
    let iconListIndex = 0;

    let processedContent = content;

    // Process galleries first
    processedContent = processedContent.replace(
      galleryRegex,
      (match, galleryContent) => {

        // Extract image URLs from the gallery content
        const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
        const images = [];
        let imageMatch;

        while ((imageMatch = imageRegex.exec(galleryContent)) !== null) {
          images.push({
            alt: imageMatch[1] || "",
            src: imageMatch[2],
          });
        }


        if (images.length > 0) {
          galleries[galleryIndex] = images;
          const marker = `\n\n__GALLERY_${galleryIndex++}__\n\n`;
          return marker;
        }
        return match;
      }
    );

    // Process icon lists
    processedContent = processedContent.replace(
      iconListRegex,
      (match, iconListContent) => {

        iconLists[iconListIndex] = iconListContent;
        const marker = `\n\n__ICON_LIST_${iconListIndex++}__\n\n`;
        return marker;
      }
    );


    return { content: processedContent, galleries, iconLists };
  };

  // Custom component for rendering galleries
  const GalleryComponent = ({ galleryIndex, galleries }) => {
    const images = galleries[galleryIndex];
    if (!images || images.length === 0) {
      return null;
    }

    const getGridCols = (count) => {
      if (count === 1) return "grid-cols-1";
      if (count === 2) return "grid-cols-1 md:grid-cols-2";
      if (count === 3) return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
      return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
    };

    return (
      <div className={`grid ${getGridCols(images.length)} gap-4 my-8`}>
        {images.map((image, index) => {
          return (
            <div key={index} className="w-full">
              <MarkdownImage
                src={image.src}
                alt={image.alt}
                className="w-full rounded-sm object-cover"
                style={{ margin: 0 }}
              />
            </div>
          );
        })}
      </div>
    );
  };

  // Smart image component for markdown that handles both Cloudinary and Strapi images
  const MarkdownImage = ({ src, alt, ...props }) => {
    // Check if it's a full Cloudinary URL - use it directly since it's already optimized
    if (src?.includes("cloudinary.com")) {
      return (
        <img
          src={src}
          alt={alt}
          className="w-full max-w-[600px] rounded-sm my-6"
          loading="lazy"
          {...props}
        />
      );
    }

    // Handle any full URLs (external images or direct Cloudinary)
    if (src?.startsWith("http")) {
      return (
        <img
          src={src}
          alt={alt}
          className="w-full rounded-sm my-6"
          loading="lazy"
          {...props}
        />
      );
    }

    // Handle relative Strapi paths (though with Cloudinary plugin these should be rare)
    if (src?.startsWith("/uploads/")) {
      const strapiUrl =
        process.env.REACT_APP_STRAPI_URL || "http://localhost:1337";

      return (
        <img
          src={`${strapiUrl}${src}`}
          alt={alt}
          className="w-full rounded-sm my-6"
          loading="lazy"
          {...props}
        />
      );
    }

    // Fallback for any other format
    return (
      <img
        src={src}
        alt={alt}
        className="w-full rounded-sm my-6"
        loading="lazy"
        {...props}
      />
    );
  };

  // Function to render icon list
  const renderIconList = (content) => {
    // Parse the markdown content to extract list items
    const lines = content.trim().split("\n");
    const items = [];

    for (let line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine.startsWith("-") || trimmedLine.startsWith("*")) {
        // Remove the bullet point and add to items
        items.push(trimmedLine.substring(1).trim());
      } else if (trimmedLine.length > 0) {
        // If it's not empty and doesn't start with bullet, treat as item
        items.push(trimmedLine);
      }
    }

    // Function to process basic markdown formatting
    const processMarkdown = (text) => {
      return text
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // Bold
        .replace(/\*(.*?)\*/g, "<em>$1</em>") // Italic
        .replace(/`(.*?)`/g, "<code>$1</code>"); // Code
    };

    return (
      <div className="mt-8 mb-16">
        <ul className="space-y-2">
          {items.map((item, index) => (
            <li
              key={index}
              className="flex items-start gap-3 text-base sm:text-lg font-light leading-relaxed bg-[var(--color-secondary-dark)] p-4 rounded-sm max-w-3xl"
              style={{ color: "var(--color-primary)" }}
            >
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="text-[var(--color-success)] w-5 h-5 mt-1 flex-shrink-0"
              />
              <span
                dangerouslySetInnerHTML={{ __html: processMarkdown(item) }}
              />
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (!loading && !post) {
    return (
      <div className="min-h-screen bg-[var(--color-primary)] flex items-center justify-center">
        <div className="text-center">
          <Headline variant="h2" color="secondary">
            Artikel nicht gefunden
          </Headline>
          <Button onClick={handleBackClick} variant="outline" className="mt-4">
            Zurück
          </Button>
        </div>
      </div>
    );
  }

  // Don't render anything while loading or if no post
  if (!post) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | WTM Sportwagen Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.tags.join(", ")} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.heroImage} />d
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <article className="min-h-screen bg-[var(--color-primary)] pb-24">
        {/* Header Section with Title and Meta */}
        <Section variant="primary" spacing="compact" margin={false}>
          <ContentBlock maxWidth="7xl" className="px-6 pt-8 mx-auto">
            {/* Article Header */}
            <header className="mb-8">
              <Headline
                variant="splitscreen"
                color="secondary"
                className="text-center leading-tight mb-6 font-bold"
              >
                {post.title}
              </Headline>

              {/* Meta Information - Just date, centered */}
              <div className="text-center">
                <Paragraph
                  variant="medium"
                  color="secondary"
                  className="opacity-75 uppercase tracking-wider"
                >
                  {new Date(post.publishDate).toLocaleDateString("de-DE", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Paragraph>
              </div>
            </header>
          </ContentBlock>
        </Section>

        {/* Hero Image - 16:10 aspect ratio */}
        <div className="relative w-full aspect-[16/10] sm:aspect-[16/5] overflow-hidden">
          <img
            src={post.heroImage}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        {/* Content Section */}
        <Section variant="primary" spacing="compact" margin={false}>
          <ContentBlock maxWidth="4xl" className="px-6 mx-auto">
            {/* Back Button */}
            <div className="mb-8">
              <Button
                onClick={handleBackClick}
                variant="outline"
                size="small"
                className="shadow-lg"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Zurück
              </Button>
            </div>

            {/* Article Content */}
            <div className="w-full text-white space-y-6 leading-relaxed text-lg">
              {(() => {
                const {
                  content: processedContent,
                  galleries,
                  iconLists,
                } = parseContentTags(post.content);

                // Split content by gallery and icon list markers and process each part
                const contentParts = processedContent.split(
                  /(__GALLERY_\d+__|__ICON_LIST_\d+__)/
                );

                return (
                  <div>
                    {contentParts.map((part, index) => {
                      const galleryMatch = part.match(/^__GALLERY_(\d+)__$/);
                      const iconListMatch = part.match(/^__ICON_LIST_(\d+)__$/);

                      if (galleryMatch) {
                        const galleryIndex = parseInt(galleryMatch[1]);
                        return (
                          <GalleryComponent
                            key={`gallery-${galleryIndex}`}
                            galleryIndex={galleryIndex}
                            galleries={galleries}
                          />
                        );
                      } else if (iconListMatch) {
                        const iconListIndex = parseInt(iconListMatch[1]);
                        return (
                          <div key={`iconlist-${iconListIndex}`}>
                            {renderIconList(iconLists[iconListIndex])}
                          </div>
                        );
                      } else if (part.trim()) {
                        // Regular markdown content
                        return (
                          <ReactMarkdown
                            key={`content-${index}`}
                            components={{
                              h1: ({ children }) => (
                                <h1 className="text-3xl font-bold text-white mb-4 text-left">
                                  {children}
                                </h1>
                              ),
                              h2: ({ children }) => (
                                <h2 className="text-2xl font-bold text-white mb-3 mt-8 text-left">
                                  {children}
                                </h2>
                              ),
                              h3: ({ children }) => (
                                <h3 className="text-xl font-semibold text-white mb-2 mt-6 text-left">
                                  {children}
                                </h3>
                              ),
                              p: ({ children }) => (
                                <p className="text-white mb-4 leading-relaxed font-light text-base sm:text-lg">
                                  {children}
                                </p>
                              ),
                              ul: ({ children }) => (
                                <ul className="text-white list-disc list-inside space-y-2 mb-4 font-light text-base sm:text-lg">
                                  {children}
                                </ul>
                              ),
                              ol: ({ children }) => (
                                <ol className="text-white list-decimal list-inside space-y-2 mb-4 font-light text-base sm:text-lg">
                                  {children}
                                </ol>
                              ),
                              li: ({ children }) => (
                                <li className="text-white leading-relaxed font-light text-base sm:text-lg">
                                  {children}
                                </li>
                              ),
                              strong: ({ children }) => (
                                <strong className="text-white font-bold">
                                  {children}
                                </strong>
                              ),
                              em: ({ children }) => (
                                <em className="text-white italic">
                                  {children}
                                </em>
                              ),
                              img: MarkdownImage,
                              blockquote: ({ children }) => (
                                <blockquote className="border-l-4 border-[var(--color-secondary)] pl-6 my-6 italic text-white/90">
                                  {children}
                                </blockquote>
                              ),
                              code: ({ inline, children }) =>
                                inline ? (
                                  <code className="bg-gray-800 text-[var(--color-secondary)] px-2 py-1 rounded text-sm">
                                    {children}
                                  </code>
                                ) : (
                                  <pre className="bg-gray-900 text-white p-4 rounded-sm overflow-x-auto my-4">
                                    <code>{children}</code>
                                  </pre>
                                ),
                              a: ({ href, children, className, ...props }) => (
                                <a
                                  href={href}
                                  className={className || "hyperlink-dark"}
                                  {...props}
                                >
                                  {children}
                                </a>
                              ),
                            }}
                          >
                            {part}
                          </ReactMarkdown>
                        );
                      }
                      return null;
                    })}
                  </div>
                );
              })()}
            </div>
          </ContentBlock>
        </Section>
      </article>
    </>
  );
};

export default SimpleBlogPost;
